import { useEffect, useState } from "react";
import { Link } from "react-router-dom";                                                          // add this line
import { Copy, CheckCircle2, ExternalLink, Globe, Lock, ArrowUpRight, Instagram, BookOpen } from "lucide-react";  // add BookOpen here
import api from "@/lib/api";

/**
 * PublicStorefrontCard.jsx
 * ------------------------------------------------------------------
 * Drop-in card for the Catalog page: lets the merchant turn their
 * shareable public catalog page on/off and grab the link.
 * Starter plan and above only — Free plan sees a locked upsell instead
 * and never calls the settings API at all.
 * Reads/writes GET+POST /public-catalog-settings.
 * ------------------------------------------------------------------
 */
export default function PublicStorefrontCard({ plan = "free", onEnabledChange }) {
  const locked = plan === "free";
  const [loading, setLoading] = useState(!locked);
  const [enabled, setEnabled] = useState(false);
  const [showInEmails, setShowInEmails] = useState(true);
  const [shareUrl, setShareUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (locked) return; // Free plan never calls this endpoint — it's not allowed to use it anyway.
    let cancelled = false;
    api
      .get("/public-catalog-settings")
      .then(({ data }) => {
        if (cancelled) return;
        setEnabled(!!data.enabled);
        setShowInEmails(data.show_in_emails !== false);
        setShareUrl(data.share_url || "");
        onEnabledChange?.(!!data.enabled);
      })
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [locked]);

  const toggle = async () => {
    const next = !enabled;
    setSaving(true);
    setEnabled(next); // optimistic
    onEnabledChange?.(next);
    try {
      const { data } = await api.post("/public-catalog-settings", { enabled: next });
      setEnabled(!!data.enabled);
      setShareUrl(data.share_url || shareUrl);
      onEnabledChange?.(!!data.enabled);
    } catch {
      setEnabled(!next); // roll back
      onEnabledChange?.(!next);
    } finally {
      setSaving(false);
    }
  };

  const toggleShowInEmails = async () => {
    const next = !showInEmails;
    setSaving(true);
    setShowInEmails(next); // optimistic
    try {
      const { data } = await api.post("/public-catalog-settings", { enabled, show_in_emails: next });
      setShowInEmails(data.show_in_emails !== false);
    } catch {
      setShowInEmails(!next); // roll back
    } finally {
      setSaving(false);
    }
  };

  const copyLink = () => {
    if (!shareUrl) return;
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  if (locked) {
    return (
      <div className="mt-6 flex flex-wrap items-center justify-between gap-4 rounded-3xl border border-hotpink/20 bg-hotpink/5 p-6">
        <div className="flex items-center gap-3">
          <span className="grid h-11 w-11 flex-none place-items-center rounded-2xl bg-hotpink/10 text-hotpink">
            <Lock size={18} />
          </span>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">Creator, Starter, Growth & Pro</p>
            <p className="mt-0.5 text-sm text-ink/70">
              Get a shareable public catalog page — customers tap a product to message you and order.
            </p>
          </div>
        </div>
        <a
          href="/plans"
          className="cta-glow inline-flex items-center gap-2 rounded-full bg-sunset-gradient px-5 py-2 text-sm font-semibold text-white"
        >
          Upgrade <ArrowUpRight size={14} />
        </a>
      </div>
    );
  }

  if (loading) return null;

  return (
    <div className="mt-6 rounded-3xl border border-ink/10 bg-white p-6">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <span className="grid h-11 w-11 flex-none place-items-center rounded-2xl bg-lilac text-ink">
            <Globe size={18} />
          </span>
          <div className="min-w-0">
            <p className="font-bold text-ink">Public catalog page</p>
            <p className="mt-0.5 max-w-md text-sm text-ink/60">
              A shareable page showing your products with photos and prices — customers tap a product to
              message you on Instagram and order. Not searchable on Google.
            </p>
            <Link to="/public-catalog-guide" className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-hotpink hover:underline">
              <BookOpen size={12} /> How this page works & what shows per plan →
            </Link>
          </div>
        </div>

        <div className="flex flex-none items-center gap-2">
          <span className={`text-xs font-semibold uppercase tracking-wide ${enabled ? "text-hotpink" : "text-ink/40"}`}>
            {enabled ? "On" : "Off"}
          </span>
          <button
            type="button"
            onClick={toggle}
            disabled={saving}
            role="switch"
            aria-checked={enabled}
            data-testid="public-storefront-toggle"
            className={`relative inline-flex h-8 w-14 flex-none items-center rounded-full transition-colors disabled:opacity-60 ${
              enabled ? "bg-hotpink" : "bg-ink/15"
            }`}
          >
            <span
              className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition-transform ${
                enabled ? "translate-x-7" : "translate-x-1"
              }`}
            />
          </button>
        </div>
      </div>

      {enabled && shareUrl && (
        <div className="mt-4 md:pl-14">
          <div className="flex w-full items-stretch overflow-hidden rounded-2xl border border-ink/15 bg-ink/[0.02]">
            <a
              href={shareUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex min-w-0 flex-1 items-center gap-1.5 px-3 py-2.5 text-xs font-semibold text-ink hover:bg-ink/5"
            >
              <span className="truncate">{shareUrl.replace(/^https?:\/\//, "")}</span>
              <ExternalLink size={12} className="hidden flex-none sm:inline" />
            </a>
            <button
              type="button"
              onClick={copyLink}
              className="flex flex-none items-center gap-1.5 border-l border-ink/15 bg-ink px-3.5 py-2.5 text-xs font-semibold text-cream hover:bg-ink/85"
            >
              {copied ? <CheckCircle2 size={13} /> : <Copy size={13} />}
              <span className="hidden sm:inline">{copied ? "Copied" : "Copy link"}</span>
            </button>
          </div>
          <p className="mt-2 flex items-center gap-1.5 text-xs text-ink/50">
            <Instagram size={12} className="flex-none" />
            Tip: add this link to your Instagram bio so customers can browse your full catalog anytime.
          </p>
          <div className="mt-3 flex items-center gap-2">
            <span className={`text-xs font-semibold uppercase tracking-wide ${showInEmails ? "text-hotpink" : "text-ink/40"}`}>
              Show 'Visit Store' button in order emails
            </span>
            <button
              type="button"
              onClick={toggleShowInEmails}
              disabled={saving}
              role="switch"
              aria-checked={showInEmails}
              data-testid="storefront-email-link-toggle"
              className={`relative inline-flex h-6 w-11 flex-none items-center rounded-full transition-colors disabled:opacity-60 ${
                showInEmails ? "bg-hotpink" : "bg-ink/15"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                  showInEmails ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
